import { AutomationType, TriggerType, UpdateRecordOwnerType } from "helpers/enum";

// ROI-235. The email step "Send from" choice, one of three modes. Saved step JSON is unchanged:
//   SpecificUser -> isFromAtsUser + atsUserId (database user) or isFromUser + userId (Roi-AI user)
//   Owner        -> isFromOwner + ownerType
//   Custom       -> all three flags false, senderName + senderEmail typed by the user
export const SenderMode = {
  SpecificUser: "specificUser",
  Owner: "owner",
  Custom: "custom"
};

export const SpecificUserKind = {
  Database: "database",
  RoiAi: "roiAi"
};

export const SpecificUserSourceText = {
  [SpecificUserKind.Database]: "Database user",
  [SpecificUserKind.RoiAi]: "Roi-AI user"
};

export const getSenderMode = (formEmail) => {
  if (formEmail?.isFromAtsUser || formEmail?.isFromUser) return SenderMode.SpecificUser;
  if (formEmail?.isFromOwner) return SenderMode.Owner;
  return SenderMode.Custom;
};

const normaliseEmail = (email) => (email ?? "").trim().toLowerCase();

// One collator for every name sort. localeCompare with options builds a new collator on each call, which
// made the user lists slow to sort on every form render (about 57 ms for 1,000 users, 1 ms with this).
const nameCollator = new Intl.Collator(undefined, { sensitivity: "base" });
export const compareByName = (a, b) => nameCollator.compare(a ?? "", b ?? "");

const toOption = (user, kind) => ({
  key: `${kind}-${user.id}`,
  kind,
  id: user.id,
  fullName: user.fullName ?? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim(),
  email: user.email ?? ""
});

// One list for "Specific user": every database user, plus the Roi-AI users with no database user.
// A Roi-AI user is linked when a database user in the list has the same email (trimmed, any case),
// the ROI-236 rule; the list already carries only live database users of the company, as the
// resolver requires. A saved Roi-AI sender stays listed even when it is now linked, so an existing
// step keeps showing the person it sends from.
export const buildSpecificUserOptions = (atsUsers, roiUsers, keepRoiUserId = null) => {
  const databaseOptions = (Array.isArray(atsUsers) ? atsUsers : []).map(u => toOption(u, SpecificUserKind.Database));
  const linkedEmails = new Set(databaseOptions.map(o => normaliseEmail(o.email)).filter(Boolean));
  const roiOptions = (Array.isArray(roiUsers) ? roiUsers : [])
    .filter(u => !linkedEmails.has(normaliseEmail(u.email)) || u.id === keepRoiUserId)
    .map(u => toOption(u, SpecificUserKind.RoiAi));

  return [...databaseOptions, ...roiOptions]
    .sort((a, b) => compareByName(a.fullName, b.fullName));
};

export const findSelectedSpecificUser = (formEmail, options) => {
  if (formEmail?.isFromAtsUser && formEmail.atsUserId != null)
    return options.find(o => o.kind === SpecificUserKind.Database && o.id === formEmail.atsUserId) ?? null;
  if (formEmail?.isFromUser && formEmail.userId != null)
    return options.find(o => o.kind === SpecificUserKind.RoiAi && o.id === formEmail.userId) ?? null;
  return null;
};

// The logged-in person as a "Specific user" option: their database user when linked (ROI-236),
// otherwise their own Roi-AI user (ROI-235 point 3). linkedAtsUser is undefined on a backend without
// ROI-236, so the email match is the same rule applied here, lowest owner id first like the resolver.
// null only for someone in neither list, such as a super admin.
export const resolveLoggedInSpecificUser = (user, options) => {
  if (!user || !Array.isArray(options)) return null;
  const database = options.filter(o => o.kind === SpecificUserKind.Database);

  const linkedOwnerId = user.linkedAtsUser?.ownerId;
  if (linkedOwnerId != null) {
    const linked = database.find(o => o.id === linkedOwnerId);
    if (linked) return linked;
  }

  const email = normaliseEmail(user.email);
  if (email) {
    const byEmail = database
      .filter(o => normaliseEmail(o.email) === email)
      .sort((a, b) => a.id - b.id)[0];
    if (byEmail) return byEmail;
  }

  return options.find(o => o.kind === SpecificUserKind.RoiAi && o.id === user.id) ?? null;
};

// Sender fields for a "Specific user" pick. option null = the mode with nobody picked, which the
// form's validation blocks from saving. "Update database using" follows the sender the way the old
// Database user and Roi-AI user buttons set it, and only when the kind of user changes, so a value
// the user chose for the same kind is kept (ROI-237 owns that default).
export const specificUserSenderState = (option, { isBullhorn = false, previous = null } = {}) => {
  const isRoiAi = option?.kind === SpecificUserKind.RoiAi;
  const state = {
    isFromOwner: false,
    isFromAtsUser: !isRoiAi,
    atsUserId: !isRoiAi && option ? option.id : null,
    isFromUser: isRoiAi,
    userId: isRoiAi ? option.id : null
  };

  // Nobody picked leaves "Update database using" as it is (ROI-237: no linked user keeps the step's
  // current default), and a kind only counts once a user is actually picked.
  if (!option) return state;

  const previousKind = previous?.isFromUser && previous?.userId != null ? SpecificUserKind.RoiAi
    : previous?.isFromAtsUser && previous?.atsUserId != null ? SpecificUserKind.Database
    : null;
  const nextKind = isRoiAi ? SpecificUserKind.RoiAi : SpecificUserKind.Database;
  if (previousKind !== nextKind) {
    state.updateRecordType = isRoiAi
      ? (isBullhorn ? UpdateRecordOwnerType.AdminUser : UpdateRecordOwnerType.NoUser)
      : UpdateRecordOwnerType.DatabaseUser;
  }

  return state;
};

export const customSenderState = () => ({
  isFromOwner: false,
  isFromAtsUser: false,
  atsUserId: null,
  isFromUser: false,
  userId: null
});

// Owner sender names. Owner type 2 is "Job owner" in the front end but the backend runs it as
// OwnerType.PlacementOwner: the placement's owner on a placement-based trigger, the job's owner on a
// job-based one. The name follows what is actually sent.
export const getOwnerSenderLabel = (ownerType, automationType, triggerType) => {
  const isCandidate = automationType === AutomationType.Candidate;
  switch (ownerType) {
    case UpdateRecordOwnerType.JobOwner:
      return triggerType === TriggerType.PlacementBased ? "Placement owner" : "Job owner";
    case UpdateRecordOwnerType.PlacementRecruiter:
      return "Placement recruiter";
    case UpdateRecordOwnerType.MostRecentNoteOwner:
      return "Most recent note owner";
    default:
      return isCandidate ? "Recruiter" : "Contact owner";
  }
};

export const getOwnerSenderDescription = (ownerType, automationType, triggerType) => {
  const record = automationType === AutomationType.Candidate ? "candidate" : "contact";
  switch (ownerType) {
    case UpdateRecordOwnerType.JobOwner:
      return triggerType === TriggerType.PlacementBased
        ? "Emails will be sent from the owner of each placement."
        : "Emails will be sent from the owner of each job.";
    case UpdateRecordOwnerType.PlacementRecruiter:
      return "Emails will be sent from the recruiter on each placement.";
    case UpdateRecordOwnerType.MostRecentNoteOwner:
      return `Emails will be sent from whoever wrote the latest note on each ${record}.`;
    default:
      return record === "candidate"
        ? "Emails will be sent from each candidate's recruiter."
        : "Emails will be sent from each contact's owner.";
  }
};

// The address a "Specific user" email goes out from: the user's email name at the step's sending
// domain (AutomationEmailProcessor builds it the same way). senderDomain is stored as "@domain".
export const getSpecificUserSenderEmail = (option, senderDomain) => {
  const local = (option?.email ?? "").split("@")[0];
  if (!local) return "";
  return senderDomain ? `${local}${senderDomain.startsWith("@") ? "" : "@"}${senderDomain}` : option.email;
};

export const getSpecificUserLocalPart = (option) => (option?.email ?? "").split("@")[0];

// The step's sender name and email stay the run-time fallback: AutomationEmailProcessor sends with
// them when the picked user can no longer be resolved (left the ATS). While the pair is still the
// "test" placeholders, or still the previous pick's own values, write the picked person into it so
// the worst case sends under that person instead of "test". Kept as is when the email name would
// not pass the form's validator (it also gates Save while the field is hidden) or the user typed
// their own sender.
export const specificUserFallbackSender = (option, formEmail, { placeholder, isValidEmailName, previousOption = null }) => {
  if (!option || !formEmail) return {};
  const isPlaceholder = formEmail.senderName === placeholder.Name && formEmail.senderEmail === placeholder.Email;
  const isPreviousPick = !!previousOption
    && formEmail.senderName === previousOption.fullName
    && formEmail.senderEmail === getSpecificUserLocalPart(previousOption).toLowerCase();
  if (!isPlaceholder && !isPreviousPick) return {};

  const emailName = getSpecificUserLocalPart(option).toLowerCase();
  if (!option.fullName || !isValidEmailName(emailName)) return {};
  return { senderName: option.fullName, senderEmail: emailName };
};
